import * as React from "react";

import ClassNames from "classnames";
import ArrowDown from "../_assets/icon-triangle-down.svg";
import Styles from "./DropDownMenu.module.css";

import Button from "../Button";
import Link from "../Link";

export interface Props {
  text: string;
  compact?: boolean;
  behavior?: "hover" | "click";
  className?: string;
  items?: any;
  description?: string;
  id?: string;
  parentUrl?: string;
}

const DropDownMenu: React.FC<Props> = props => {
  const {
    text,
    compact = false,
    behavior = "hover",
    className,
    items = [],
    description,
    id,
    parentUrl
  } = props;
  const [active, setActive] = React.useState("");
  const [aria, setAria] = React.useState(false);

  const [position, setPosition] = React.useState<
    "right" | "left" | "top" | "bottom"
  >("bottom");
  const containerRef = React.useRef();
  const tooltipRef = React.useRef();

  React.useEffect(() => {
    let containerRect: any;
    let tooltipRect: any;
    containerRect = containerRef.current;
    tooltipRect = tooltipRef.current;

    if (tooltipRect != null) {
      containerRect = containerRect.getBoundingClientRect();
      tooltipRect = tooltipRect.getBoundingClientRect();

      const docWidth = document.documentElement.clientWidth;
      const docHeight = document.documentElement.clientHeight;

      const rx = containerRect.x + containerRect.width; // most right x
      const lx = containerRect.x; // most left x
      // const ty = containerRect.y; // most top y
      const by = containerRect.y + containerRect.height; // most bottom y

      const spaceRight = rx + tooltipRect.width <= window.scrollX + docWidth;
      const spaceLeft = lx - tooltipRect.width >= 0;
      // const spaceAbove = ty - tooltipRect.height >= 0;
      const spaceBelow = by + tooltipRect.height <= window.scrollY + docHeight;

      // dynamically set the position by available space
      if (!spaceBelow) {
        setPosition("top");
      } else if (!spaceLeft && spaceRight) {
        setPosition("left");
      } else if (!spaceRight && spaceLeft) {
        setPosition("right");
      } else {
        setPosition("bottom");
      }
    }
  }, [active]);

  function toggleDropdown() {
    setActive(active === "" ? "active" : "");
    setAria(!aria);
  }

  const iconEl = (
    <div className={Styles.icon}>
      <span className={Styles.iconDown}>{<ArrowDown is="svg" />}</span>
    </div>
  );

  return (
    <div
      className={ClassNames({
        [Styles.dropdown]: true,
        [Styles.link]: behavior === "hover",
        [Styles.active]: active,
        [className]: className
      })}
    >
      <div className={Styles.titleWrapper}>
        {behavior === "hover" ? (
          <a
            className={ClassNames({
              [Styles.title]: true,
              [Styles.link]: true
            })}
            href={parentUrl}
            onFocus={toggleDropdown}
            onMouseEnter={toggleDropdown}
            onMouseLeave={toggleDropdown}
            aria-expanded={aria}
            aria-haspopup="true"
            id={id}
            aria-controls={description}
            ref={containerRef}
          >
            {text}
            {iconEl}
          </a>
        ) : (
          <Button
            className={ClassNames({
              [Styles.title]: true,
              [Styles.button]: true
            })}
            variant="outlined"
            compact={compact}
            onClick={toggleDropdown}
            aria-expanded={aria}
            aria-haspopup="true"
            aria-controls={description}
            inputRef={containerRef}
          >
            {text}
            {iconEl}
          </Button>
        )}
      </div>
      <div
        className={ClassNames({
          [Styles.dropdownContent]: true,
          [Styles[position]]: true
        })}
        ref={tooltipRef}
      >
        <ul
          className={Styles.dropdownList}
          aria-labelledby={id}
          id={description}
        >
          {items &&
            items.map((item, i) => (
              <li key={i}>
                <Link {...item} />
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
};

export default DropDownMenu;
